import type { Ref } from "vue";

import type {
  AiAutomationSimple,
  AutomationId,
  Controller,
  OrgId,
  ReferenceId,
} from "@ctrl/controller-rpc";

import { markdownToDocument } from "@/components/editor/markdown";
import type { BaseLogger } from "@/components/inject";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { getOrganization, type UseProfile } from "@/composables/useProfile";
import { waitForRefValue } from "@/ref";
import { browser } from "@/services/browser";
import { errorMessage } from "@/utils/unknown";

/**
 * Browser-store key holding the id of the assistant automation to run after a
 * meeting transcript has been written to the recorded object's notes. An
 * empty string means the user disabled it; an absent key means the account
 * was never initialized on this device (see
 * {@link getSelectedMeetingAutomationId} for the three states).
 *
 * The key is namespaced per account (`useBrowserStore(accountId)`): the
 * automation lives in one account's personal organization, so a selection
 * must never leak to the other accounts of this profile. The key also exists
 * un-namespaced, written by older versions of the app; the first read moves
 * that legacy value into the reading account's key and deletes it (see
 * {@link getSelectedMeetingAutomationId}).
 */
export const MEETING_AUTOMATION_KEY = "preferences/meetings/automation:id";

export const DEFAULT_MEETING_AUTOMATION_NAME = "Meeting summary";

/**
 * Step prompt of the default meeting automation. The automation runs after
 * the transcript has been appended to the recorded object's notes, so it
 * reads everything it needs from the object itself. The audio attachment is
 * written afterwards, so a notes overwrite cannot drop it.
 *
 * The explicit tool guidance ("use the tool matching the id prefix") works
 * around the backend prompt builder not yet steering the model towards
 * kind-specific tools for `event:`/`task:` ids.
 */
export const DEFAULT_MEETING_AUTOMATION_PROMPT = `You are an assistant responsible for turning meeting recordings into concise, accurate and useful meeting notes.

## Input

You are given the id of the event or custom object that was recorded.

First, fetch the input object using the tool matching its id prefix:
- "event:XXX" → getEvent
- "task:XXX" → getTask
- "page:XXX" → getPage
- "object:XXX" → getObject

The object's notes may contain:
- notes written manually by the user;
- a "Transcript" section containing the recording transcript;
- attachments, embeds or other existing content.

The object's title and other available context may also help you understand the meeting.

## Goal

Generate a concise summary using the transcript, the object's title/context, and any manual notes.

Treat the user's manual notes as strong signals of what they considered important.

Only state information supported by the transcript, manual notes or object context. Do not invent facts, decisions, commitments, action items, owners, deadlines, names or numbers.

The transcript may contain speech-to-text errors, missing punctuation, repetition or false starts. Resolve obvious errors from context, but do not guess when the meaning is genuinely unclear.

## Summary format

Create an H1 "Summary" section.

Start with a short overview of the most important outcomes of the meeting, including key conclusions and any clear decisions.

When useful, add H2 sections for the major topics discussed. Group related discussion together rather than creating a section for every minor topic.

Use concise bullet points. Avoid chronological play-by-play, small talk, repetition, tangents and inconsequential details.

Only describe something as a decision if the source clearly indicates it was agreed or decided.

If there are clear next steps or commitments, add an H2 "Action Items" section at the end and represent each action item as a checklist item.

For action items:
- include only genuine commitments or clearly requested next steps;
- include an owner only when supported by the source;
- include a deadline only when one was actually specified;
- preserve relative dates exactly as stated, such as "tomorrow" or "next Tuesday";
- do not turn suggestions or possibilities into action items.

If there are no clear action items, omit the "Action Items" section.

Use H2 and H3 headings inside the Summary when useful, but never another H1 heading.

For short or low-content transcripts, keep the summary correspondingly short.

Write the summary in the dominant language of the meeting. Preserve names, product names and technical terminology in their original form.

## Preserve existing notes

Do not modify the user's existing content.

The final notes must contain:
1. The H1 "Summary" section.
2. All previous non-generated content, reproduced verbatim and in exactly the same order.

Preserve every existing block exactly, including manual notes, the Transcript section, attachments, embeds, headings and formatting.

If the notes already begin with a generated H1 "Summary" section, replace it instead of adding another one.

## Save

Before saving, verify that:
- no existing user content was edited or removed;
- there is exactly one top-level "Summary" section;
- no unsupported decisions or action items were introduced;
- the transcript remains unchanged.

Then overwrite the object's notes using the tool matching the object's kind:
- event → updateEvent
- task → updateTask
- page → updatePage
- custom object → updateNotesColumn`;

/**
 * The account's stored meeting-automation selection, one of three states:
 *
 * - `undefined` — never initialized for this account on this device;
 * - `""` — the user disabled it ("No automation" in Settings → Meetings);
 * - an automation id — the selection, to be validated before use.
 */
export function getSelectedMeetingAutomationId(
  profile: Ref<UseProfile>
): string | undefined {
  const store = useBrowserStore(profile.value.user.accountId);
  // One-time migration: move the profile-wide value written before the key
  // was account-namespaced into this account's key, deleting the legacy key
  // so it is never read again (every later read hits the account's key). If
  // this account is not the one the selection belongs to, the usual
  // validation discards the moved id. The legacy key was written raw by
  // older versions; the namespaced key is JSON like the rest of the store.
  if (!store.has(MEETING_AUTOMATION_KEY)) {
    const legacy = browser.store.getRaw(MEETING_AUTOMATION_KEY, "");
    if (legacy) {
      browser.store.unset(MEETING_AUTOMATION_KEY);
      store.set(MEETING_AUTOMATION_KEY, legacy);
    }
  }
  return store.get<string | undefined>(MEETING_AUTOMATION_KEY, undefined);
}

/**
 * Persist the selected meeting automation id for the account. Always writes
 * the account-namespaced key — including the empty id ("no automation"): the
 * key being absent means "never written for this account", which lets a read
 * migrate the legacy un-namespaced value in; writing "" instead of removing
 * the key makes a cleared selection final (a not-yet-migrated legacy value
 * cannot resurrect it). Every writer of the key must go through this helper
 * (and every remover through {@link unsetSelectedMeetingAutomationId}).
 */
export function setSelectedMeetingAutomationId(
  profile: Ref<UseProfile>,
  automationId: string
): void {
  useBrowserStore(profile.value.user.accountId).set(
    MEETING_AUTOMATION_KEY,
    automationId
  );
}

/**
 * Discard a stored selection that positively failed validation (deleted
 * automation, id inherited from another account through the legacy
 * un-namespaced key, malformed value). Removes the key entirely, returning
 * the account to the "never initialized" state so the launch-time
 * {@link setDefaultMeetingAutomation} seeds a fresh default — unlike the
 * user's explicit "No automation" choice, which must be persisted as `""`
 * through {@link setSelectedMeetingAutomationId} and is never re-seeded.
 *
 * Removing the key cannot resurrect the legacy un-namespaced value: the read
 * that produced the id being discarded already migrated and deleted it.
 */
export function unsetSelectedMeetingAutomationId(
  profile: Ref<UseProfile>
): void {
  useBrowserStore(profile.value.user.accountId).unset(MEETING_AUTOMATION_KEY);
}

type StoredAutomationLookup =
  /** The stored id resolves to an automation. */
  | { outcome: "found"; automation: AiAutomationSimple }
  /** The stored id points at nothing (deleted, another account's, or
   * malformed): the selection must be discarded. */
  | { outcome: "missing" }
  /** The lookup failed transiently: the stored id could not be verified and
   * must be kept as-is. */
  | { outcome: "unavailable"; error: unknown };

/**
 * Fetch an automation by the *stored* id. The store is not a trusted source
 * of ids: a malformed value (e.g. free text that leaked into the store
 * through a picker bug) makes the RPC client throw a schema validation error
 * before even looking the automation up — that means the stored id itself is
 * garbage and reports as `missing`, not as a crash. Any other rejection is a
 * transient failure and reports as `unavailable` so callers do not wrongly
 * discard a still-valid selection.
 */
async function getStoredAutomation(
  controller: Controller,
  organizationId: OrgId,
  automationId: string
): Promise<StoredAutomationLookup> {
  try {
    const automation = (
      await controller.ai.automation.get(organizationId, automationId)
    ).release();
    return automation
      ? { outcome: "found", automation }
      : { outcome: "missing" };
  } catch (error) {
    // Fixed message shape of the RPC client's argument decoding (see
    // controller_client.ml): `rpc "...": invalid argument "automation_id": …`.
    if (errorMessage(error).includes('invalid argument "automation_id"')) {
      return { outcome: "missing" };
    }
    return { outcome: "unavailable", error };
  }
}

/**
 * Whether `automationId` still resolves to an automation of this account's
 * personal organization. It can point at nothing when the automation was
 * deleted, or when the id came through the legacy un-namespaced key and
 * belongs to another account of this profile.
 *
 * Pure check: reads nothing from and writes nothing to the selection store —
 * callers decide what to do with an invalid id. Returns `false` only when
 * the lookup *positively* resolved to nothing; a transient lookup failure or
 * a personal organization that has not replicated yet (fresh account)
 * returns `true`, so a possibly-valid selection is never treated as invalid
 * just because it could not be verified.
 */
export async function validateMeetingAutomationId(
  controller: Controller,
  profile: Ref<UseProfile>,
  automationId: string
): Promise<boolean> {
  // The id is always derivable from the profile, but the organization must
  // have *replicated* before the id can be verified: on a fresh account the
  // lookup would wrongly report a valid selection as missing.
  const personalOrgId = profile.value.user.personal.organizationId;
  if (!getOrganization(profile.value, personalOrgId)) {
    return true;
  }

  const lookup = await getStoredAutomation(
    controller,
    personalOrgId,
    automationId
  );
  // Not `=== "found"`: the third outcome, "unavailable" (transient lookup
  // failure), must count as valid, or a controller hiccup would make
  // setDefaultMeetingAutomation duplicate the automation and Meetings.vue
  // clear the user's selection.
  return lookup.outcome !== "missing";
}

/**
 * Run the user-selected assistant automation on the recorded object, if any.
 *
 * The automation is owned by the assistant, so it lives in the personal
 * organization but can operate on objects from any organization.
 */
export async function runSelectedMeetingAutomation(
  controller: Controller,
  baseLogger: BaseLogger,
  profile: Ref<UseProfile>,
  objectId: ReferenceId
): Promise<void> {
  const logger = baseLogger.forModule("recording/meetingAutomation");

  const automationId = getSelectedMeetingAutomationId(profile);
  if (!automationId) {
    logger.info("no meeting automation selected, skipping", { objectId });
    return;
  }

  const assistantId = (await controller.ai.assistant.get()).release();
  const personalOrgId = profile.value.user.personal.organizationId;
  const personalOrg = getOrganization(profile.value, personalOrgId);
  if (!assistantId || !personalOrg) {
    logger.error("could not resolve assistant or personal organization", {
      automationId,
      hasAssistant: !!assistantId,
      hasPersonalOrg: !!personalOrg,
    });
    return;
  }

  // The user may have deleted the selected automation since picking it (or
  // the id came through the legacy un-namespaced key and belongs to another
  // account of this profile, or is malformed); this is a legitimate state,
  // not a processing failure. The dangling id is left in the store — never
  // overwritten with "" (that means "user disabled") — so the launch-time
  // setDefaultMeetingAutomation replaces it with a fresh default.
  const lookup = await getStoredAutomation(
    controller,
    personalOrgId,
    automationId
  );
  if (lookup.outcome === "unavailable") {
    logger.error("could not verify the selected meeting automation, skipping", {
      automationId,
      personalOrgId,
      error: lookup.error,
    });
    return;
  }
  if (lookup.outcome === "missing") {
    logger.info("selected meeting automation not found (deleted?), skipping", {
      automationId,
      personalOrgId,
    });
    return;
  }
  const { automation } = lookup;
  if (automation.enabled === false) {
    logger.info("selected meeting automation is disabled, skipping", {
      automationId,
    });
    return;
  }

  logger.info("running meeting automation", {
    automationId,
    personalOrgId,
    objectId,
  });
  const runId = await controller.ai.automation.run.runManually(
    personalOrgId,
    automationId,
    assistantId,
    objectId
  );
  logger.info("meeting automation run started", { automationId, runId });
  void controller.metric.send(
    { object: "ai_recording", properties: { action: "automation_applied" } },
    {}
  );
}

/**
 * Create the default "Meeting summary" automation, owned by the assistant in
 * the personal organization, and return its id.
 */
export async function createDefaultMeetingAutomation(
  controller: Controller,
  baseLogger: BaseLogger,
  profile: Ref<UseProfile>
): Promise<AutomationId> {
  const assistantId = (await controller.ai.assistant.get()).release();
  const personalOrgId = profile.value.user.personal.organizationId;
  if (!assistantId || !getOrganization(profile.value, personalOrgId)) {
    throw new Error(
      "Could not resolve the assistant or the personal organization"
    );
  }

  const stepId = await controller.ai.step.create(
    personalOrgId,
    {
      name: "Summarize the meeting",
      prompt: {
        context: {},
        document: markdownToDocument(
          DEFAULT_MEETING_AUTOMATION_PROMPT,
          baseLogger
        ),
      },
    },
    null
  );

  return controller.ai.automation.create(
    personalOrgId,
    DEFAULT_MEETING_AUTOMATION_NAME,
    [stepId],
    assistantId
  );
}

/**
 * Ensure a meeting automation is selected for this account. Runs at every
 * app launch (see Routine.vue), so it covers accounts that predate the
 * sign-up seeding, fresh devices (the selection is stored per device), a
 * seeding attempt that failed, and a selected automation that was deleted:
 *
 * - a selection that still resolves wins and nothing is created;
 * - an explicitly cleared selection ("No automation" in the Meetings
 *   settings) is respected and never re-seeded;
 * - a selection that no longer resolves is replaced with a fresh default.
 *
 * The attempt reacts to the assistant and personal organization becoming
 * available at any point during the session (both are provisioned
 * server-side and can take arbitrarily long to replicate after account
 * creation), so on a brand-new account it completes whenever replication
 * lands; if the app quits before then, the next launch retries.
 */
export async function setDefaultMeetingAutomation(
  controller: Controller,
  baseLogger: BaseLogger,
  profile: Ref<UseProfile>
): Promise<void> {
  const logger = baseLogger.forModule("recording/meetingAutomation");

  // Both verifying the stored id and creating the default need the
  // assistant and the replicated personal organization. For established
  // accounts (including one that explicitly disabled the automation) both
  // have replicated long ago and the waits resolve immediately.
  const assistantRef = await controller.ai.assistant.get();
  try {
    await Promise.all([
      waitForRefValue(assistantRef, (id) => !!id),
      // The profile is reactive: the personal organization appears in it
      // once replicated.
      waitForRefValue(profile, (p) =>
        Boolean(getOrganization(p, p.user.personal.organizationId))
      ),
    ]);
  } finally {
    assistantRef.release();
  }

  // Read after the waits, so a selection made or disabled while waiting is
  // seen, and verify it now that the personal organization is available: an
  // id that no longer resolves (deleted automation, id inherited from
  // another account through the legacy un-namespaced key) is replaced
  // below. A disabled selection ("" — "No automation" in Settings →
  // Meetings) is the user's deliberate choice and is never re-seeded;
  // `undefined` means never initialized and proceeds to the seeding.
  const storedId = getSelectedMeetingAutomationId(profile);
  if (storedId === "") {
    logger.info("meeting automation explicitly disabled, not re-seeding");
    return;
  }
  if (
    storedId &&
    (await validateMeetingAutomationId(controller, profile, storedId))
  ) {
    logger.info("a meeting automation is already selected, skipping");
    return;
  }

  const automationId = await createDefaultMeetingAutomation(
    controller,
    baseLogger,
    profile
  );
  setSelectedMeetingAutomationId(profile, automationId);
  logger.info("created and selected the default meeting automation", {
    automationId,
  });
}
